//
// # srvLogger
//
// The entire SRV observability surface: run-invocation logs, run-failure logs,
// results-cleared events, and batched violation-interaction counts. Implements
// the `SrvLogger` interface that `SrvModel` forwards events to, plus a
// `dispose()` for the SrvPanel to call on unmount.
//
// All SRV logging lives here on the TS side (no Rust/server log lines), routed
// through one writer. The IDE is browser-client code, so per the logging rules
// it may use console logging — here via CoreUtils `logger.log`, with every line
// tagged `{ feature: "srv" }` for easy filtering.
//
// Interaction clicks are not logged individually: they accumulate in an
// in-memory batch (module-private to each logger instance) that is flushed on a
// 60s timer, on panel-hide, and on dispose / beforeunload, so the metrics
// reflect attention without one line per click.
//

import { logger } from "CoreUtils";
import type { SrvLogger, SrvRunResult } from "./SrvModel";

const FLUSH_INTERVAL_MS = 60_000;

// A logger handle: the events SrvModel forwards, plus lifecycle control owned by
// the SrvPanel (created on mount, disposed on unmount).
export interface SrvLoggerHandle extends SrvLogger {
  dispose(): void;
}

interface InteractionBatch {
  windowStartMs: number;
  panelClicks: number;
  canvasClicks: number;
  distinctViolationIndices: Set<number>;
  runId: string;
}

function emit(event: string, fields: Record<string, unknown>): void {
  logger.log({ feature: "srv", event, ...fields });
}

export function createSrvLogger(): SrvLoggerHandle {
  let visible = false;
  let batch = freshBatch("");

  function freshBatch(runId: string): InteractionBatch {
    return {
      windowStartMs: Date.now(),
      panelClicks: 0,
      canvasClicks: 0,
      distinctViolationIndices: new Set(),
      runId,
    };
  }

  // Emit the batch (only if it carries any clicks), then reset the window.
  function flushBatch(): void {
    if (batch.panelClicks > 0 || batch.canvasClicks > 0) {
      emit("interaction_batch", {
        window_ms: Date.now() - batch.windowStartMs,
        run_id: batch.runId,
        panel_clicks: batch.panelClicks,
        canvas_clicks: batch.canvasClicks,
        distinct_violations: batch.distinctViolationIndices.size,
      });
    }
    batch = freshBatch(batch.runId);
  }

  // When a click arrives for a different run than the batch is tracking, flush
  // the prior run's counts before switching.
  function ensureRun(runId: string): void {
    if (runId !== batch.runId) {
      flushBatch();
      batch = freshBatch(runId);
    }
  }

  const timer = setInterval(() => {
    if (visible) flushBatch();
  }, FLUSH_INTERVAL_MS);

  const onBeforeUnload = (): void => {
    flushBatch();
  };
  // Browser only — tests run under node where `window` is undefined.
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", onBeforeUnload);
  }

  return {
    logRunInvocation(
      run: SrvRunResult,
      elapsedMs: number,
      runId: string
    ): void {
      // Any clicks pending from the previous run belong to that run — flush
      // before this run becomes the batch's subject.
      flushBatch();
      batch = freshBatch(runId);

      const counts: Record<string, number> = {};
      for (const violation of run.violations) {
        counts[violation.ruleId] = (counts[violation.ruleId] ?? 0) + 1;
      }
      emit("run", {
        run_id: runId,
        ran_rule_ids: run.ranRuleIds,
        violated_rule_ids: Object.keys(counts),
        violation_counts: counts,
        total_violations: run.violations.length,
        passed: run.violations.length === 0,
        elapsed_ms: elapsedMs,
      });
    },

    logRunFailed(why: string, runId: string): void {
      emit("run_failed", { run_id: runId, why });
    },

    recordPanelClick(violationIndex: number, runId: string): void {
      ensureRun(runId);
      batch.panelClicks += 1;
      batch.distinctViolationIndices.add(violationIndex);
    },

    recordCanvasClick(violationIndex: number, runId: string): void {
      ensureRun(runId);
      batch.canvasClicks += 1;
      batch.distinctViolationIndices.add(violationIndex);
    },

    setPanelVisible(nextVisible: boolean): void {
      // Flush the window when the panel is hidden (clicks can't happen while
      // hidden, so a partial window would otherwise sit unreported).
      if (visible && !nextVisible) flushBatch();
      visible = nextVisible;
    },

    logResultsCleared(
      hadResults: boolean,
      violationCountAtClear: number
    ): void {
      emit("results_cleared", {
        had_results: hadResults,
        violation_count_at_clear: violationCountAtClear,
      });
    },

    dispose(): void {
      flushBatch();
      clearInterval(timer);
      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", onBeforeUnload);
      }
    },
  };
}
